import { useCallback, useMemo, useRef, useState } from 'react';
import type { Person } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Phase 3E-1 — right-side slide-in drawer for editing the top-level overview
 * fields on an external candidate. Opened from the "Edit overview" button in
 * CandidateProfileModal's header (available from any tab).
 *
 * Contract:
 *  - Only fields that actually changed since the drawer opened are sent.
 *  - Backend stamps `projection_locks[field] = 'admin_edit'` per field so the
 *    next CV re-import leaves manual values alone.
 *  - Unsaved-changes guard fires on close / backdrop click.
 *  - On success, calls `onSaved(updatedPerson)` so the parent patches its
 *    local state + can trigger a pool-list refresh.
 *
 * `availability_date` is admin-only on the server. The drawer shows the field
 * and relies on the backend 403 for non-admins; `isAdmin` prop (optional)
 * is used to hide the input pre-emptively to keep the UI tidy.
 *
 * Wireframe: docs/designs/candidate-edit-flows.svg (section A).
 */

import type { OverviewFieldsPatch } from '../../services/api';
import { adminProfileApi, type ProfileMutationsApi } from '../../services/profileMutationsApi';
import { looksLikeEmail } from '../../utils/looksLikeEmail';

export interface CandidateOverviewEditDrawerProps {
  person: Person;
  apiClient?: ProfileMutationsApi;
  onClose: () => void;
  onSaved: (updated: Person) => void;
  /** When false, the availability_date input is hidden from the drawer. */
  isAdmin?: boolean;
  /**
   * When false (magic-link / future logged-in self-service), contact email is shown read-only.
   * Identity is keyed off this email; changing it would break auth matching.
   */
  allowEditContactEmail?: boolean;
}

const SENIORITY_OPTIONS = ['intern', 'junior', 'mid', 'senior', 'lead', 'principal'] as const;
const AVAILABILITY_OPTIONS = ['available', 'open', 'not_looking'] as const;
const LOCATION_MODEL_OPTIONS = ['onsite', 'hybrid', 'remote'] as const;
const EMPLOYMENT_TYPE_OPTIONS = ['fulltime', 'contract', 'freelance', 'internship', 'other'] as const;

type FormState = {
  name: string;
  contact: string;
  linkedin_profile_url: string;
  title: string;
  primary_location: string;
  professional_summary: string;
  seniority_level: string;
  management_level: string;
  education_level: string;
  availability_status: string;
  availability_date: string;
  notice_period_weeks: string;
  location_model_preferences: string[];
  employment_type_preferences: string[];
  years_experience_total: string;
  professional_interests: string;
  job_responsibilities: string;
};

function initialFormState(person: Person): FormState {
  const d = person.CandidateDetails;
  return {
    name: person.Name ?? '',
    contact: person.Contact ?? '',
    linkedin_profile_url: person.LinkedInProfileUrl ?? '',
    title: person.Title ?? '',
    primary_location: person.PrimaryLocation ?? '',
    professional_summary: d?.professionalSummary ?? '',
    seniority_level: (d?.seniorityLevel as string) ?? '',
    management_level: person.ManagementLevel ?? '',
    education_level: d?.educationLevel ?? '',
    availability_status: (d?.availability.status as string) ?? '',
    availability_date: d?.availability.date ?? '',
    notice_period_weeks:
      d?.availability.noticePeriodWeeks != null
        ? String(d.availability.noticePeriodWeeks)
        : '',
    location_model_preferences: [...(d?.locationModelPreferences ?? [])],
    employment_type_preferences: [...(d?.employmentTypePreferences ?? [])],
    years_experience_total:
      d?.yearsExperienceTotal != null ? String(d.yearsExperienceTotal) : '',
    professional_interests: person.ProfessionalInterests ?? '',
    job_responsibilities: person.JobResponsibilities ?? '',
  };
}

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Compares the mutated form state against the baseline and produces the
 * narrowest possible PATCH payload. Empty strings on text/date/enum fields
 * become `null` (clears the column on the server).
 */
function diffPatch(initial: FormState, current: FormState): OverviewFieldsPatch {
  const out: OverviewFieldsPatch = {};
  if (initial.name !== current.name) out.name = current.name.trim() || null;
  if (initial.contact !== current.contact) out.contact = current.contact.trim() || null;
  if (initial.linkedin_profile_url !== current.linkedin_profile_url) out.linkedin_profile_url = current.linkedin_profile_url.trim() || null;
  if (initial.title !== current.title) out.title = current.title.trim() || null;
  if (initial.primary_location !== current.primary_location)
    out.primary_location = current.primary_location.trim() || null;
  if (initial.professional_summary !== current.professional_summary)
    out.professional_summary = current.professional_summary.trim() || null;
  if (initial.seniority_level !== current.seniority_level) {
    out.seniority_level = current.seniority_level
      ? (current.seniority_level as OverviewFieldsPatch['seniority_level'])
      : null;
  }
  if (initial.management_level !== current.management_level)
    out.management_level = current.management_level.trim() || null;
  if (initial.education_level !== current.education_level)
    out.education_level = current.education_level.trim() || null;
  if (initial.availability_status !== current.availability_status) {
    out.availability_status = current.availability_status
      ? (current.availability_status as OverviewFieldsPatch['availability_status'])
      : null;
  }
  if (initial.availability_date !== current.availability_date)
    out.availability_date = current.availability_date || null;
  if (initial.notice_period_weeks !== current.notice_period_weeks) {
    const n = current.notice_period_weeks.trim();
    out.notice_period_weeks = n === '' ? null : Number(n);
  }
  if (!arraysEqual(initial.location_model_preferences, current.location_model_preferences)) {
    out.location_model_preferences = current.location_model_preferences as OverviewFieldsPatch['location_model_preferences'];
  }
  if (!arraysEqual(initial.employment_type_preferences, current.employment_type_preferences)) {
    out.employment_type_preferences = current.employment_type_preferences as OverviewFieldsPatch['employment_type_preferences'];
  }
  if (initial.years_experience_total !== current.years_experience_total) {
    const n = current.years_experience_total.trim();
    out.years_experience_total = n === '' ? null : Number(n);
  }
  if (initial.professional_interests !== current.professional_interests) {
    out.professional_interests = current.professional_interests.trim() || null;
  }
  if (initial.job_responsibilities !== current.job_responsibilities) {
    out.job_responsibilities = current.job_responsibilities.trim() || null;
  }
  return out;
}

/** Employees only PATCH contact + LinkedIn via /api/people/:id (server rejects other overview keys). */
function pickEmployeeIdentityPatch(patch: OverviewFieldsPatch): OverviewFieldsPatch {
  const out: OverviewFieldsPatch = {};
  if (patch.contact !== undefined) out.contact = patch.contact;
  if (patch.linkedin_profile_url !== undefined) out.linkedin_profile_url = patch.linkedin_profile_url;
  return out;
}

export const CandidateOverviewEditDrawer: React.FC<CandidateOverviewEditDrawerProps> = ({
  person,
  apiClient = adminProfileApi,
  onClose,
  onSaved,
  isAdmin = false,
  allowEditContactEmail = true,
}) => {
  const initial = useMemo(() => initialFormState(person), [person]);
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);

  const isExternalCandidate = useMemo(
    () => (person.ProfileCategory || '').toLowerCase() === 'external_candidate',
    [person.ProfileCategory]
  );

  /** Magic-link / session: employee can edit key facts (not sign-in email). Admin HR: contact + LinkedIn only. */
  const isEmployeeSelfServiceKeyFacts = !isExternalCandidate && !allowEditContactEmail;
  const showKeyFactsFields = isExternalCandidate || isEmployeeSelfServiceKeyFacts;
  const showCandidateProfileFields = isExternalCandidate;

  const buildPatchForSave = useCallback((): OverviewFieldsPatch => {
    let patch = diffPatch(initial, form);
    if (!allowEditContactEmail) {
      delete patch.contact;
    }
    if (!isExternalCandidate && !isEmployeeSelfServiceKeyFacts) {
      patch = pickEmployeeIdentityPatch(patch);
    }
    return patch;
  }, [initial, form, allowEditContactEmail, isExternalCandidate, isEmployeeSelfServiceKeyFacts]);

  const isDirty = useMemo(() => Object.keys(buildPatchForSave()).length > 0, [buildPatchForSave]);

  const confirmClose = useCallback(
    (nextAction: () => void) => {
      if (!isDirty) {
        nextAction();
        return;
      }
      const ok = window.confirm(
        'You have unsaved changes. Discard them and close the drawer?'
      );
      if (ok) nextAction();
    },
    [isDirty]
  );

  const nameFieldRef = useRef<HTMLInputElement | null>(null);
  const emailFieldRef = useRef<HTMLInputElement | null>(null);
  const asideTrapRef = useRef<HTMLElement | null>(null);
  const overviewFocusRef = isExternalCandidate ? nameFieldRef : emailFieldRef;
  useFocusTrap(asideTrapRef, true, { initialFocusRef: overviewFocusRef });

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldError((prev) => (prev?.field === key ? null : prev));
  };

  const toggleChip = (key: 'location_model_preferences' | 'employment_type_preferences', value: string) => {
    setForm((prev) => {
      const cur = prev[key];
      return {
        ...prev,
        [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
      };
    });
  };

  const handleSave = async () => {
    if (!person.id || saving) return;
    const patch = buildPatchForSave();
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    if (allowEditContactEmail && patch.contact !== undefined && patch.contact !== null) {
      const c = String(patch.contact).trim();
      if (c !== '' && !looksLikeEmail(c)) {
        setFieldError({ field: 'contact', message: 'Enter a valid email address' });
        return;
      }
    }
    setSaving(true);
    setServerError(null);
    setFieldError(null);
    try {
      const updated = await apiClient.patchOverviewFields(person.id, patch);
      onSaved(updated);
    } catch (e: any) {
      const data = e?.response?.data ?? {};
      const message = data.error || e?.message || 'Failed to save changes';
      if (typeof data.field === 'string') {
        setFieldError({ field: data.field, message });
      } else {
        setServerError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      data-testid="candidate-overview-edit-drawer"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="flex-1 bg-black bg-opacity-50"
        data-testid="candidate-overview-edit-backdrop"
        aria-hidden="true"
      />
      <aside
        ref={asideTrapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="overview-edit-drawer-title"
        className="w-full max-w-[560px] bg-dark-card border-l border-dark-border h-full overflow-y-auto"
      >
        <div className="p-6 border-b border-dark-border sticky top-0 bg-dark-card z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 id="overview-edit-drawer-title" className="text-lg font-bold text-dark-text">
                Edit key facts
              </h2>
              <p className="text-xs text-dark-text-secondary mt-1">
                {isExternalCandidate ? (
                  <>
                    Changes are stamped as <code className="text-amber-200">admin_edit</code> and
                    locked against CV re-import.
                  </>
                ) : isEmployeeSelfServiceKeyFacts ? (
                  <>
                    Update the facts shown in the Key facts panel. Your sign-in email cannot be changed
                    here.
                  </>
                ) : (
                  <>Update work email and LinkedIn for this employee. Other profile fields are maintained elsewhere.</>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => confirmClose(onClose)}
              className="text-dark-text-secondary hover:text-dark-text text-2xl leading-none"
              aria-label="Close drawer"
              data-testid="candidate-overview-edit-close"
            >
              &times;
            </button>
          </div>
        </div>

        <form
          className="p-6 space-y-5"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {isExternalCandidate && (
            <>
              <Field label="Name" fieldId="overview-edit-name" error={fieldError?.field === 'name' ? fieldError.message : null}>
                <input
                  ref={nameFieldRef}
                  id="overview-edit-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  className={inputClass}
                  maxLength={255}
                />
              </Field>

              <Field label="Title" fieldId="overview-edit-title" error={fieldError?.field === 'title' ? fieldError.message : null}>
                <input
                  id="overview-edit-title"
                  type="text"
                  value={form.title}
                  onChange={(e) => update('title', e.target.value)}
                  className={inputClass}
                  maxLength={500}
                />
              </Field>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email" fieldId="overview-edit-contact" error={fieldError?.field === 'contact' ? fieldError.message : null}>
              <input
                ref={emailFieldRef}
                id="overview-edit-contact"
                type="email"
                value={form.contact}
                onChange={(e) => update('contact', e.target.value)}
                className={inputClass}
                maxLength={500}
                disabled={!allowEditContactEmail}
                readOnly={!allowEditContactEmail}
                aria-readonly={allowEditContactEmail ? undefined : true}
              />
              {!allowEditContactEmail ? (
                <p className="text-xs text-dark-text-secondary mt-1">
                  Email is your sign-in identity and cannot be changed here. Ask an admin if it needs updating.
                </p>
              ) : null}
            </Field>

            <Field label="LinkedIn profile URL" fieldId="overview-edit-linkedin" error={fieldError?.field === 'linkedin_profile_url' ? fieldError.message : null}>
              <input
                id="overview-edit-linkedin"
                type="url"
                value={form.linkedin_profile_url}
                onChange={(e) => update('linkedin_profile_url', e.target.value)}
                className={inputClass}
                maxLength={2048}
                placeholder="https://linkedin.com/in/..."
              />
            </Field>
          </div>

          {showKeyFactsFields && (
          <section
            className="space-y-4 rounded-lg border border-indigo-500/30 bg-dark-bg/50 p-4"
            data-testid="overview-edit-profile-narrative"
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-300">
              Profile narrative
            </h3>
            <Field
              label="Professional summary"
              fieldId="overview-edit-summary"
              error={fieldError?.field === 'professional_summary' ? fieldError.message : null}
              hint="Shown on the Overview tab. Short profile blurb — not your job history."
            >
              <textarea
                id="overview-edit-summary"
                value={form.professional_summary}
                onChange={(e) => update('professional_summary', e.target.value)}
                rows={4}
                className={`${inputClass} resize-y`}
                maxLength={4000}
                placeholder="e.g. Startup mentor, CIO architecture background, 20+ years in IT"
              />
            </Field>

            {isEmployeeSelfServiceKeyFacts && (
              <>
                <Field
                  label="Interests"
                  fieldId="overview-edit-interests"
                  error={
                    fieldError?.field === 'professional_interests' ? fieldError.message : null
                  }
                  hint="Career interests, domains, and hobbies (shown in Key facts)."
                >
                  <textarea
                    id="overview-edit-interests"
                    value={form.professional_interests}
                    onChange={(e) => update('professional_interests', e.target.value)}
                    rows={3}
                    className={`${inputClass} resize-y`}
                    maxLength={8000}
                    placeholder="e.g. Fintech, mentoring, cycling"
                  />
                </Field>

                <Field
                  label="Current role summary"
                  fieldId="overview-edit-responsibilities"
                  error={
                    fieldError?.field === 'job_responsibilities' ? fieldError.message : null
                  }
                  hint="Short description of your current role only. Past roles are on the Experience tab."
                >
                  <textarea
                    id="overview-edit-responsibilities"
                    value={form.job_responsibilities}
                    onChange={(e) => update('job_responsibilities', e.target.value)}
                    rows={3}
                    className={`${inputClass} resize-y`}
                    maxLength={8000}
                    placeholder="What you do in your current position"
                  />
                </Field>
              </>
            )}
          </section>
          )}

          {showKeyFactsFields && (
          <Field
            label="Primary location"
            fieldId="overview-edit-location"
            error={fieldError?.field === 'primary_location' ? fieldError.message : null}
          >
            <input
              id="overview-edit-location"
              type="text"
              value={form.primary_location}
              onChange={(e) => update('primary_location', e.target.value)}
              className={inputClass}
              maxLength={200}
            />
          </Field>
          )}

          {showKeyFactsFields && (
          <div
            className={`grid grid-cols-1 gap-4 ${showCandidateProfileFields ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
          >
            {showCandidateProfileFields && (
            <Field
              label="Seniority"
              fieldId="overview-edit-seniority"
              error={fieldError?.field === 'seniority_level' ? fieldError.message : null}
            >
              <select
                id="overview-edit-seniority"
                value={form.seniority_level}
                onChange={(e) => update('seniority_level', e.target.value)}
                className={inputClass}
              >
                <option value="">—</option>
                {SENIORITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            )}

            <Field
              label={showCandidateProfileFields ? 'Management' : 'Management level'}
              fieldId="overview-edit-mgmt"
              error={fieldError?.field === 'management_level' ? fieldError.message : null}
            >
              <input
                id="overview-edit-mgmt"
                type="text"
                value={form.management_level}
                onChange={(e) => update('management_level', e.target.value)}
                className={inputClass}
                maxLength={40}
                placeholder="e.g. manager, director"
              />
            </Field>

            <Field
              label={showCandidateProfileFields ? 'Education' : 'Education level'}
              fieldId="overview-edit-education"
              error={fieldError?.field === 'education_level' ? fieldError.message : null}
            >
              <input
                id="overview-edit-education"
                type="text"
                value={form.education_level}
                onChange={(e) => update('education_level', e.target.value)}
                className={inputClass}
                maxLength={40}
                placeholder="e.g. bachelor, master"
              />
            </Field>
          </div>
          )}

          {showKeyFactsFields && (
          <Field
            label="Experience total (years)"
            fieldId="overview-edit-years-exp"
            error={fieldError?.field === 'years_experience_total' ? fieldError.message : null}
          >
            <input
              id="overview-edit-years-exp"
              type="number"
              min={0}
              max={80}
              step={1}
              value={form.years_experience_total}
              onChange={(e) => update('years_experience_total', e.target.value)}
              className={inputClass}
            />
          </Field>
          )}

          {showKeyFactsFields && (
          <Field
            label="Location model preferences"
            fieldId="overview-edit-loc-prefs"
            error={
              fieldError?.field === 'location_model_preferences' ? fieldError.message : null
            }
          >
            <ChipToggleGroup
              id="overview-edit-loc-prefs"
              options={LOCATION_MODEL_OPTIONS as unknown as readonly string[]}
              selected={form.location_model_preferences}
              onToggle={(v) => toggleChip('location_model_preferences', v)}
            />
          </Field>
          )}

          {showKeyFactsFields && (
          <Field
            label="Employment type preferences"
            fieldId="overview-edit-emp-prefs"
            error={
              fieldError?.field === 'employment_type_preferences' ? fieldError.message : null
            }
          >
            <ChipToggleGroup
              id="overview-edit-emp-prefs"
              options={EMPLOYMENT_TYPE_OPTIONS as unknown as readonly string[]}
              selected={form.employment_type_preferences}
              onToggle={(v) => toggleChip('employment_type_preferences', v)}
            />
          </Field>
          )}

          {showKeyFactsFields && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field
              label="Availability"
              fieldId="overview-edit-avail"
              error={fieldError?.field === 'availability_status' ? fieldError.message : null}
            >
              <select
                id="overview-edit-avail"
                value={form.availability_status}
                onChange={(e) => update('availability_status', e.target.value)}
                className={inputClass}
              >
                <option value="">—</option>
                {AVAILABILITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>

            {isAdmin && (
              <Field
                label="Available from"
                fieldId="overview-edit-avail-date"
                error={
                  fieldError?.field === 'availability_date' ? fieldError.message : null
                }
              >
                <input
                  id="overview-edit-avail-date"
                  type="date"
                  value={form.availability_date}
                  onChange={(e) => update('availability_date', e.target.value)}
                  className={inputClass}
                />
              </Field>
            )}

            <Field
              label="Notice (weeks)"
              fieldId="overview-edit-notice"
              error={
                fieldError?.field === 'notice_period_weeks' ? fieldError.message : null
              }
            >
              <input
                id="overview-edit-notice"
                type="number"
                min={0}
                max={52}
                step={1}
                value={form.notice_period_weeks}
                onChange={(e) => update('notice_period_weeks', e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
          )}

          {serverError && (
            <p
              className="text-sm text-red-400"
              role="alert"
              data-testid="candidate-overview-edit-error"
            >
              {serverError}
            </p>
          )}

          <div className="pt-4 border-t border-dark-border flex items-center justify-between gap-3">
            <p className="text-xs text-dark-text-secondary">
              {isDirty
                ? 'Unsaved changes'
                : 'No changes yet.'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => confirmClose(onClose)}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-dark-bg border border-dark-border text-dark-text-secondary hover:text-dark-text disabled:opacity-50"
                data-testid="candidate-overview-edit-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isDirty || saving}
                className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="candidate-overview-edit-save"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small internal building blocks
// ---------------------------------------------------------------------------

const inputClass =
  'w-full rounded-md bg-dark-bg border border-dark-border px-3 py-2 text-sm text-dark-text placeholder-dark-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-indigo-500';

const Field: React.FC<{
  label: string;
  fieldId: string;
  error: string | null;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, fieldId, error, hint, children }) => (
  <div>
    <label htmlFor={fieldId} className="block text-xs font-medium text-dark-text-secondary mb-1.5">
      {label}
    </label>
    {hint ? (
      <p className="text-xs text-dark-text-secondary/80 mb-1.5">{hint}</p>
    ) : null}
    {children}
    {error && (
      <p className="text-xs text-red-400 mt-1" role="alert" data-testid={`field-error-${fieldId}`}>
        {error}
      </p>
    )}
  </div>
);

const ChipToggleGroup: React.FC<{
  id: string;
  options: readonly string[];
  selected: readonly string[];
  onToggle: (value: string) => void;
}> = ({ id, options, selected, onToggle }) => (
  <div id={id} role="group" className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const isOn = selected.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onToggle(opt)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            isOn
              ? 'bg-indigo-600 border-indigo-500 text-white'
              : 'bg-dark-bg border-dark-border text-dark-text-secondary hover:text-dark-text'
          }`}
          aria-pressed={isOn}
          data-testid={`chip-toggle-${opt}`}
        >
          {opt.replace('_', ' ')}
        </button>
      );
    })}
  </div>
);

/** Internal helper exported for unit tests only. */
export const __test__ = { diffPatch, arraysEqual, initialFormState, pickEmployeeIdentityPatch };

export default CandidateOverviewEditDrawer;
